import axios from 'axios';
import { API } from '../global/global';
import { 
  DispositivoSsemResponse, 
  DispositivoSsemSingleResponse,
  DispositivoSsemEstadisticasResponse,
  TagsDisponiblesResponse,
  DispositivoSsemFormData,
  DispositivoSsemConUbicacionResponse
} from '../interfaces/DispositivoSsemInterface';

// Función para obtener todos los dispositivos Skyangel con paginación
export const getAllDispositivosSsem = async (
  page: number = 1, 
  perPage: number = 10, 
  search?: string
): Promise<DispositivoSsemResponse> => {
  console.log('🔄 Obteniendo dispositivos Skyangel...');
  
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    
    const response = await axios.get(`${API}/dispositivos-ssem?${params.toString()}`, {
      timeout: 10000
    });

    console.log('✅ Dispositivos Skyangel obtenidos exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo dispositivos Skyangel:', error);
    
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 404) {
        throw new Error('Endpoint de dispositivos no encontrado');
      }
      if (status && status >= 500) {
        throw new Error('Error del servidor al obtener dispositivos');
      }
    }
    
    throw new Error('Error al obtener dispositivos Skyangel');
  }
};

// Función para obtener un dispositivo específico
export const getDispositivoSsem = async (id: number): Promise<DispositivoSsemSingleResponse> => {
  console.log(`🔄 Obteniendo dispositivo Skyangel ${id}...`);
  
  try {
    const response = await axios.get(`${API}/dispositivos-ssem/${id}`, {
      timeout: 10000
    });

    console.log('✅ Dispositivo Skyangel obtenido exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo dispositivo Skyangel:', error);
    
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 404) {
        throw new Error('Dispositivo no encontrado');
      }
      if (status && status >= 500) {
        throw new Error('Error del servidor al obtener dispositivo');
      }
    }
    
    throw new Error('Error al obtener dispositivo Skyangel');
  }
};

// Función para crear un nuevo dispositivo
export const createDispositivoSsem = async (data: DispositivoSsemFormData): Promise<DispositivoSsemSingleResponse> => {
  console.log('🔄 Creando dispositivo Skyangel...', data);
  
  try {
    const response = await axios.post(`${API}/dispositivos-ssem`, data, {
      timeout: 15000
    });

    console.log('✅ Dispositivo Skyangel creado exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error creando dispositivo Skyangel:', error);
    
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 422) {
        // Errores de validación
        throw {
          message: 'Errores de validación',
          errors: error.response?.data?.errors || {}
        };
      }
      if (status && status >= 500) {
        throw new Error('Error del servidor al crear dispositivo');
      }
    }
    
    throw new Error('Error al crear dispositivo Skyangel');
  }
};

// Función para actualizar un dispositivo
export const updateDispositivoSsem = async (
  id: number, 
  data: DispositivoSsemFormData
): Promise<DispositivoSsemSingleResponse> => {
  console.log(`🔄 Actualizando dispositivo Skyangel ${id}...`, data);
  
  try {
    const response = await axios.put(`${API}/dispositivos-ssem/${id}`, data, {
      timeout: 15000
    });

    console.log('✅ Dispositivo Skyangel actualizado exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error actualizando dispositivo Skyangel:', error);
    
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 422) {
        // Errores de validación
        throw {
          message: 'Errores de validación',
          errors: error.response?.data?.errors || {}
        };
      }
      if (status === 404) {
        throw new Error('Dispositivo no encontrado');
      }
      if (status && status >= 500) {
        throw new Error('Error del servidor al actualizar dispositivo');
      }
    }
    
    throw new Error('Error al actualizar dispositivo Skyangel');
  }
};

// Función para obtener estadísticas de dispositivos
export const getDispositivosSsemEstadisticas = async (): Promise<DispositivoSsemEstadisticasResponse> => {
  console.log('🔄 Obteniendo estadísticas de dispositivos Skyangel...');
  
  try {
    const response = await axios.get(`${API}/dispositivos-ssem/estadisticas`, {
      timeout: 10000
    });

    console.log('✅ Estadísticas obtenidas exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas:', error);
    throw new Error('Error al obtener estadísticas de dispositivos');
  }
};

// Función para obtener tags disponibles para asignar
export const getTagsDisponibles = async (): Promise<TagsDisponiblesResponse> => {
  console.log('🔄 Obteniendo tags disponibles...');
  
  try {
    const response = await axios.get(`${API}/dispositivos-ssem/tags-disponibles`, {
      timeout: 10000
    });

    console.log('✅ Tags disponibles obtenidas exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo tags disponibles:', error);
    throw new Error('Error al obtener tags disponibles');
  }
};

// Función para obtener tags disponibles para editar (incluye la tag actual)
export const getTagsDisponiblesParaEditar = async (dispositivoId: number): Promise<TagsDisponiblesResponse> => {
  console.log(`🔄 Obteniendo tags disponibles para editar dispositivo ${dispositivoId}...`);
  
  try {
    const response = await axios.get(`${API}/dispositivos-ssem/${dispositivoId}/tags-disponibles`, {
      timeout: 10000
    });

    console.log('✅ Tags disponibles para editar obtenidas exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo tags disponibles para editar:', error);
    throw new Error('Error al obtener tags disponibles para editar');
  }
};

// Función para obtener dispositivos Skyangel con ubicación (para el mapa)
export const getDispositivosSsemConUbicacion = async (): Promise<DispositivoSsemConUbicacionResponse> => {
  console.log('🔄 Obteniendo dispositivos Skyangel con ubicación para el mapa...');
  
  try {
    // Crear una instancia de axios sin interceptores para evitar problemas de autenticación
    const axiosInstance = axios.create({
      baseURL: API,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });

    const response = await axiosInstance.get('/dispositivos-ssem/con-ubicacion');

    console.log('✅ Dispositivos Skyangel con ubicación obtenidos exitosamente:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error obteniendo dispositivos Skyangel con ubicación:', error);
    if (axios.isAxiosError(error)) {
      console.error('❌ Detalles del error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
    }
    throw new Error('Error al obtener dispositivos Skyangel con ubicación');
  }
};
